import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { track } from '../lib/analytics';

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative overflow-hidden"
      style={{
        minHeight: '380px',
        backgroundColor: '#070707',
        backgroundImage: 'linear-gradient(180deg, rgba(12,12,12,0.9) 0%, rgba(6,6,6,1) 40%, rgba(5,5,5,1) 100%)',
      }}
    >
      {/* Micro-texture grain — matching header */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.035,
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'repeat',
          backgroundSize: '256px 256px',
        }}
      />
      {/* Warm ambient glow behind logo */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 18% 35%, rgba(194,168,120,0.025) 0%, transparent 45%)',
      }} />
      {/* Top edge glow */}
      <div className="absolute top-0 left-0 right-0 pointer-events-none" style={{
        height: '1px',
        background: 'linear-gradient(90deg, transparent 5%, rgba(194,168,120,0.08) 30%, rgba(194,168,120,0.08) 70%, transparent 95%)',
      }} />

      <div
        className="max-w-7xl mx-auto relative z-10"
        style={{ paddingTop: '88px', paddingBottom: '64px', paddingLeft: '48px', paddingRight: '48px' }}
      >
        {/* Trust strip — "Speak with the founder" call CTA.
            Replaces the old desktop floating call button without competing
            with the Emergent platform badge. */}
        <a
          href={`tel:${contactInfo.phone.replace(/[^+\d]/g, '')}`}
          data-testid="footer-call-strip"
          aria-label={`Call Toronto Royale — ${contactInfo.phone}`}
          className="footer-call-strip"
          onClick={() => track('click_call_now', { location: 'footer_strip', phone: contactInfo.phone })}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '18px',
            padding: '22px 28px',
            marginBottom: '64px',
            background: 'linear-gradient(90deg, rgba(194,168,120,0.04) 0%, rgba(194,168,120,0.07) 50%, rgba(194,168,120,0.04) 100%)',
            border: '1px solid rgba(194,168,120,0.18)',
            borderLeft: '2px solid rgba(194,168,120,0.5)',
            textDecoration: 'none',
            color: 'inherit',
            transition: 'border-color 320ms ease, background 320ms ease, transform 220ms ease',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'rgba(194,168,120,0.45)';
            e.currentTarget.style.background = 'linear-gradient(90deg, rgba(194,168,120,0.06) 0%, rgba(194,168,120,0.12) 50%, rgba(194,168,120,0.06) 100%)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'rgba(194,168,120,0.18)';
            e.currentTarget.style.background = 'linear-gradient(90deg, rgba(194,168,120,0.04) 0%, rgba(194,168,120,0.07) 50%, rgba(194,168,120,0.04) 100%)';
          }}
        >
          <span style={{
            fontSize: '10px',
            color: 'rgba(194,168,120,0.7)',
            letterSpacing: '3px',
            textTransform: 'uppercase',
            fontWeight: 400,
          }}>
            Prefer to speak with us?
          </span>

          <span aria-hidden="true" style={{
            width: '24px',
            height: '1px',
            background: 'rgba(194,168,120,0.35)',
          }} />

          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <Phone size={14} style={{ color: '#C2A878' }} strokeWidth={1.6} />
            <span className="font-serif" style={{
              fontSize: '17px',
              fontWeight: 400,
              color: '#F5F5F5',
              letterSpacing: '0.3px',
            }}>
              {contactInfo.phone}
            </span>
          </span>

          <span aria-hidden="true" className="footer-call-strip-divider" style={{
            width: '24px',
            height: '1px',
            background: 'rgba(194,168,120,0.35)',
          }} />

          <span style={{
            fontSize: '11px',
            color: 'rgba(220,220,220,0.55)',
            fontWeight: 300,
            letterSpacing: '0.3px',
          }}>
            Founder picks up · Mon–Sun · 24/7
          </span>
        </a>

        {/* 4-Column Grid — Brand wider, 3 right columns equal */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12"
          style={{ rowGap: '48px' }}
        >
          {/* Brand Column — spans 4 of 12 */}
          <div className="lg:col-span-4" style={{ paddingRight: '48px' }}>
            <img
              src="/logo-tr.png"
              alt="Toronto Royale"
              data-testid="footer-logo"
              width="200"
              height="80"
              loading="lazy"
              decoding="async"
              style={{
                height: '80px',
                width: 'auto',
                opacity: 0.92,
              }}
            />
            <p style={{
              fontSize: '13px',
              fontWeight: 300,
              lineHeight: '1.9',
              color: 'rgba(225,225,225,0.85)',
              maxWidth: '260px',
              marginTop: '28px',
            }}>
              A boutique chauffeur service built on trust, care, and genuine human connection.
            </p>
          </div>

          {/* Navigate Column — spans 2 of 12 */}
          <div className="lg:col-span-2">
            <h4 style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#C2A878',
              marginBottom: '32px',
              letterSpacing: '0.5px',
            }}>
              Navigate
            </h4>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { to: '/', label: 'Home' },
                { to: '/services', label: 'Services' },
                { to: '/about', label: 'About' },
                { to: '/blog', label: 'Blog' },
                { to: '/contact', label: 'Contact' },
                { to: '/faq', label: 'FAQ' }
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                    style={{
                      fontSize: '13px',
                      fontWeight: 300,
                      color: '#CCCCCC',
                      textDecoration: 'none',
                      transition: 'color 180ms ease',
                    }}
                    onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }}
                    onMouseLeave={(e) => { e.target.style.color = '#CCCCCC'; }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column — spans 3 of 12 */}
          <div className="lg:col-span-3">
            <h4 style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#C2A878',
              marginBottom: '32px',
              letterSpacing: '0.5px',
            }}>
              Chauffeur Service
            </h4>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { label: 'Overview', to: '/chauffeur' },
                { label: 'Our Fleet', to: '/chauffeur/fleet' },
                { label: 'Request a Quote', to: '/chauffeur/rates' },
                { label: 'Book a Chauffeur', to: '/chauffeur/book' },
                { label: 'Service Areas', to: '/chauffeur/services' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-chauffeur-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    style={{
                      fontSize: '13px',
                      fontWeight: 300,
                      color: '#CCCCCC',
                      textDecoration: 'none',
                      transition: 'color 180ms ease',
                    }}
                    onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }}
                    onMouseLeave={(e) => { e.target.style.color = '#CCCCCC'; }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column — spans 3 of 12 */}
          <div className="lg:col-span-3">
            <h4 style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#C2A878',
              marginBottom: '32px',
              letterSpacing: '0.5px',
            }}>
              Reach Out
            </h4>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <li className="flex items-start" style={{ gap: '12px' }}>
                <Phone size={14} style={{ color: 'rgba(194,168,120,0.5)', marginTop: '3px', flexShrink: 0 }} />
                <a href={`tel:${contactInfo.phone.replace(/[^+\d]/g, '')}`} data-testid="footer-phone-link" onClick={() => track('click_call_now', { location: 'footer_column', phone: contactInfo.phone })} style={{ fontSize: '13px', fontWeight: 300, color: '#CCCCCC', textDecoration: 'none', transition: 'color 180ms ease' }} onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }} onMouseLeave={(e) => { e.target.style.color = '#CCCCCC'; }}>{contactInfo.phone}</a>
              </li>
              <li className="flex items-start" style={{ gap: '12px' }}>
                <Mail size={14} style={{ color: 'rgba(194,168,120,0.5)', marginTop: '3px', flexShrink: 0 }} />
                <a href={`mailto:${contactInfo.email}`} data-testid="footer-email-link" style={{ fontSize: '13px', fontWeight: 300, color: '#CCCCCC', textDecoration: 'none', wordBreak: 'break-all', transition: 'color 180ms ease' }} onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }} onMouseLeave={(e) => { e.target.style.color = '#CCCCCC'; }}>{contactInfo.email}</a>
              </li>
              <li className="flex items-start" style={{ gap: '12px' }}>
                <MapPin size={14} style={{ color: 'rgba(194,168,120,0.5)', marginTop: '3px', flexShrink: 0 }} />
                <a href="https://maps.google.com/?q=Greater+Toronto+Area" target="_blank" rel="noopener noreferrer" data-testid="footer-map-link" style={{ fontSize: '13px', fontWeight: 300, color: '#CCCCCC', textDecoration: 'none', transition: 'color 180ms ease' }} onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }} onMouseLeave={(e) => { e.target.style.color = '#CCCCCC'; }}>{contactInfo.serviceArea}</a>
              </li>
              <li className="flex items-start" style={{ gap: '12px' }}>
                <Instagram size={14} style={{ color: 'rgba(194,168,120,0.5)', marginTop: '3px', flexShrink: 0 }} />
                <a
                  href="https://www.instagram.com/toronto_royale/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  style={{ fontSize: '13px', fontWeight: 300, color: '#CCCCCC', textDecoration: 'none', transition: 'color 180ms ease' }}
                  onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }}
                  onMouseLeave={(e) => { e.target.style.color = '#CCCCCC'; }}
                >
                  @toronto_royale
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider + Legal Line */}
        <div style={{ marginTop: '72px' }}>
          <div style={{
            width: '100%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(194,168,120,0.1) 20%, rgba(194,168,120,0.1) 80%, transparent 100%)',
          }} />

          {/* Membership Badges — NLA + OLOA */}
          <div
            data-testid="footer-memberships"
            className="flex flex-col md:flex-row items-center justify-center"
            style={{ paddingTop: '28px', paddingBottom: '8px', gap: '14px', flexWrap: 'wrap' }}
          >
            <span style={{ fontSize: '9px', color: 'rgba(194,168,120,0.5)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 400 }}>
              Proud Member
            </span>
            <a
              href="https://www.limo.org/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-nla-badge"
              className="trust-badge"
              aria-label="Visit the National Limousine Association official website (opens in a new tab)"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 18px',
                border: '1px solid rgba(194,168,120,0.25)',
                background: 'rgba(194,168,120,0.04)',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              <img
                src="/badges/nla-white.webp"
                alt="National Limousine Association"
                width="44"
                height="16"
                loading="lazy"
                decoding="async"
                style={{ height: '16px', width: 'auto', opacity: 0.9 }}
              />
              <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(194,168,120,0.4)' }} />
              <span style={{ fontSize: '11px', color: 'rgba(220,220,220,0.7)', fontWeight: 300, letterSpacing: '0.3px' }}>
                National Limousine Association · Member 2025
              </span>
            </a>
            <a
              href="https://www.oloa.ca/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-oloa-badge"
              className="trust-badge"
              aria-label="Visit the Ontario Limousine Owners Association official website (opens in a new tab)"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 18px',
                border: '1px solid rgba(194,168,120,0.25)',
                background: 'rgba(194,168,120,0.04)',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '11px', color: '#C2A878', letterSpacing: '2px', fontWeight: 500 }}>OLOA</span>
              <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(194,168,120,0.4)' }} />
              <span style={{ fontSize: '11px', color: 'rgba(220,220,220,0.7)', fontWeight: 300, letterSpacing: '0.3px' }}>
                Ontario Limousine Owners Association · Member in Good Standing
              </span>
            </a>
            <a
              href="/toronto-royale-credentials.pdf"
              download
              data-testid="footer-credentials-pdf"
              style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '2px', textTransform: 'uppercase', textDecoration: 'none', borderBottom: '1px solid rgba(194,168,120,0.25)', paddingBottom: '2px', transition: 'color 180ms ease' }}
              onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }}
              onMouseLeave={(e) => { e.target.style.color = 'rgba(194,168,120,0.6)'; }}
            >
              Download Credentials PDF
            </a>
          </div>

          <div
            className="flex flex-col md:flex-row justify-between items-center"
            style={{ paddingTop: '20px', gap: '12px' }}
          >
            <p style={{ fontSize: '12px', fontWeight: 400, color: 'rgba(180,180,180,0.85)' }}>
              &copy; {new Date().getFullYear()} Toronto Royale. Proudly serving the Greater Toronto Area.
            </p>
            <div className="flex items-center" style={{ gap: '24px' }}>
              <Link
                to="/terms"
                data-testid="footer-link-terms"
                style={{ fontSize: '11px', fontWeight: 300, color: 'rgba(180,180,180,0.8)', textDecoration: 'none', letterSpacing: '0.5px', transition: 'color 180ms ease' }}
                onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(180,180,180,0.8)'; }}
              >
                Terms & Conditions
              </Link>
              <Link
                to="/privacy"
                data-testid="footer-link-privacy"
                style={{ fontSize: '11px', fontWeight: 300, color: 'rgba(180,180,180,0.8)', textDecoration: 'none', letterSpacing: '0.5px', transition: 'color 180ms ease' }}
                onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(180,180,180,0.8)'; }}
              >
                Privacy Policy
              </Link>
              <Link
                to="/cookies"
                data-testid="footer-link-cookies"
                style={{ fontSize: '11px', fontWeight: 300, color: 'rgba(180,180,180,0.8)', textDecoration: 'none', letterSpacing: '0.5px', transition: 'color 180ms ease' }}
                onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }}
                onMouseLeave={(e) => { e.target.style.color = 'rgba(180,180,180,0.8)'; }}
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
